/**
 * Batching queue for DELIVERY acks.
 *
 * Only covers what the server cannot see for itself. A message written to this user's own socket
 * is stamped delivered server-side, so the queue exists for the catch-up case: messages that
 * arrived while this client was offline and are first seen in a history fetch. Nothing is replayed
 * to a reconnecting socket, so without this those messages would never be marked delivered.
 *
 * Debounced and deduplicated because a history load can surface a hundred at once, and every
 * subsequent load surfaces the same ones again.
 */

const DEBOUNCE_MS = 250;
// Ceiling on how long a steady trickle can defer the flush.
const MAX_WAIT_MS = 1000;
// Bounds on the two sets. Both drop oldest-first: the backlog because a client offline long enough
// to exceed it is better served by the next history sweep, the acked set because it is only an
// optimisation.
const BACKLOG_MAX = 500;
const ACKED_MAX = 5000;

// messageId -> threadId, waiting for the next flush.
const pending = new Map();
// Ids acked in this session, so repeated history sweeps do not re-send.
const acked = new Set();

let debounceTimer = null;
let maxWaitTimer = null;
let sender = null;

/**
 * Installs the function that actually sends. Injected rather than imported so the queue stays
 * testable and free of a socket dependency.
 *
 * @param {(messageIds: string[], threadId: string) => boolean} fn returns false when the send
 *   did not go out, in which case the ids stay queued for the next flush.
 */
export function setDeliveredAckSender(fn) {
  sender = fn;
}

function trimOldest(collection, max) {
  while (collection.size > max) {
    const oldest = collection.keys().next().value;
    collection.delete(oldest);
  }
}

function clearTimers() {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (maxWaitTimer) {
    clearTimeout(maxWaitTimer);
    maxWaitTimer = null;
  }
}

/** Sends everything queued, grouped by thread so each frame carries a thread scope. */
export function flushDeliveredAcks() {
  clearTimers();
  if (!pending.size || typeof sender !== 'function') {
    return;
  }

  const byThread = new Map();
  for (const [messageId, threadId] of pending) {
    const key = threadId || '';
    if (!byThread.has(key)) {
      byThread.set(key, []);
    }
    byThread.get(key).push(messageId);
  }

  for (const [threadId, ids] of byThread) {
    if (sender(ids, threadId)) {
      for (const id of ids) {
        pending.delete(id);
        acked.add(id);
      }
    }
    // A failed send leaves the ids pending; the reconnect flush retries them.
  }
  trimOldest(acked, ACKED_MAX);
}

function schedule() {
  if (!debounceTimer) {
    debounceTimer = setTimeout(flushDeliveredAcks, DEBOUNCE_MS);
  } else {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(flushDeliveredAcks, DEBOUNCE_MS);
  }
  if (!maxWaitTimer) {
    maxWaitTimer = setTimeout(flushDeliveredAcks, MAX_WAIT_MS);
  }
}

/**
 * Queues one message id for a delivery ack. Callers are expected to have gated on
 * `shouldAckDelivered` first; this only handles dedupe and scheduling.
 */
export function queueDeliveredAck(messageId, threadId) {
  const id = String(messageId || '').trim();
  if (!id || acked.has(id) || pending.has(id)) {
    return;
  }
  pending.set(id, String(threadId || '').trim());
  trimOldest(pending, BACKLOG_MAX);
  schedule();
}

/**
 * Drops all queued and remembered ids. Must run on logout or profile switch, or the next user
 * inherits the previous one's dedupe set and never acks messages they actually received.
 */
export function resetDeliveredAckQueue() {
  clearTimers();
  pending.clear();
  acked.clear();
}

/** Test/diagnostic view of the queue. */
export function deliveredAckQueueSize() {
  return { pending: pending.size, acked: acked.size };
}
